/**
 * glør Varianten-Galerie — bestimmt, welche Galeriebilder ab Position 2
 * (Index 1) erscheinen, abhaengig von der gewaehlten Variante UND der
 * Storefront-Sprache. Position 1 (Variantenbild) bleibt IMMER unveraendert.
 *
 * Nachfolger von glr-locale-gallery: dieselbe, bewaehrte Tausch-Mechanik gegen
 * Horizons Galerie, aber die Bildliste haengt jetzt an der Variante statt nur
 * an der Sprache. Ohne Metaobjekt-Eintraege verhaelt sich der Block wie frueher
 * (englische Bilder aus custom.bild_englisch).
 *
 * Update-sicher & additiv: KEIN Eingriff in die Core-Galerie-Dateien. Es werden
 * nur `src`/`srcset`/`alt` und die Zoom-High-Res-Quelle (data-max-resolution)
 * bestehender <img>-Elemente ueberschrieben — in ALLEN Darstellungen der
 * Galerie (Slideshow-Slides, Grid, Zoom-Dialog) sowie in den Thumbnails.
 *
 * Robust gegen Varianten-Neuaufbau: Horizon ersetzt bei Variantenwechsel die
 * ganze <media-gallery>. Ein MutationObserver faengt das ab und bespielt die
 * frische Galerie erneut — dann mit der Liste der neuen Variante.
 *
 * Vorladen: die Bilder der uebrigen Varianten werden im Leerlauf mit niedriger
 * Prioritaet in den Browser-Cache geholt, damit der Wechsel ohne Nachladen
 * wirkt. Abschaltbar ueber die Block-Einstellung.
 */

class GlrVariantMedia extends HTMLElement {
  connectedCallback() {
    const cfg = this.#readData();
    this.entries = cfg && Array.isArray(cfg.entries) ? cfg.entries : [];
    // Produktweite englische Fassungen der gemeinsamen Bilder. Sie ersetzen die
    // Eintraege nicht, sondern fuellen hinter ihnen auf.
    this.shared = cfg && Array.isArray(cfg.shared) ? cfg.shared : [];
    this.wantPreload = !cfg || cfg.preload !== false;
    if (!this.entries.length && !this.shared.length) return;

    this.section = this.closest('.shopify-section') || document.body;
    this._raf = 0;
    const vid = this.#currentVariantId();
    this.data = this.#listFor(vid);
    this._stamp = 'v' + (vid || '0');

    this.#apply();
    this.#observe();
    this.#listenVariant();
    this.#preloadRest();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    if (this._raf) cancelAnimationFrame(this._raf);
    if (this.section && this._pselName && this._onProductSelect) {
      this.section.removeEventListener(this._pselName, this._onProductSelect);
    }
  }

  /** @returns {object|null} */
  #readData() {
    const el = this.querySelector('script[type="application/json"][data-glr-vm]');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent || 'null');
    } catch (e) {
      return null;
    }
  }

  /**
   * Aktuelle Variante: bevorzugt das versteckte Feld des Produktformulars,
   * das Horizon bei jedem Wechsel mitschreibt. Fallback ist der URL-Parameter.
   * @returns {string}
   */
  #currentVariantId() {
    const scope = this.section instanceof Element ? this.section : document;
    const input =
      scope.querySelector('input[name="id"]') || document.querySelector('input[name="id"]');
    const fromInput = input && input.value ? String(input.value).trim() : '';
    if (fromInput) return fromInput;
    try {
      return new URL(window.location.href).searchParams.get('variant') || '';
    } catch (e) {
      return '';
    }
  }

  /**
   * Bilder fuer eine Variante: alle Eintraege, deren Variantenliste sie
   * enthaelt (v === null heisst "gilt immer"), nach `o` sortiert und
   * aneinandergehaengt.
   * @param {string} vid
   * @returns {Array}
   */
  #listFor(vid) {
    const id = String(vid || '');
    const hits = this.entries.filter((e) => {
      if (!e || !Array.isArray(e.img) || !e.img.length) return false;
      if (e.v === null || e.v === undefined) return true;
      if (!id || !Array.isArray(e.v)) return false;
      // Im Metafeld stehen GIDs (gid://shopify/ProductVariant/123), im Formular
      // die nackte Zahl. Nur den letzten Pfadabschnitt exakt vergleichen.
      return e.v.some((x) => String(x).split('/').pop() === id);
    });
    hits.sort((a, b) => (Number(a.o) || 0) - (Number(b.o) || 0));
    const out = [];
    for (const e of hits) out.push(...e.img);

    // Danach die produktweiten EN-Bilder, ohne Dubletten. So bleiben die
    // englischen Fassungen der gemeinsamen Bilder erhalten, waehrend die
    // variantenspezifischen vorne stehen.
    if (this.shared.length) {
      const seen = new Set(out.map((e) => e && e.src));
      for (const e of this.shared) {
        if (e && e.src && !seen.has(e.src)) {
          out.push(e);
          seen.add(e.src);
        }
      }
    }
    return out;
  }

  /** @returns {Element|null} */
  #findGallery() {
    const scope = this.section instanceof Element ? this.section : document;
    return scope.querySelector('media-gallery') || document.querySelector('media-gallery');
  }

  /**
   * Sammelt alle positions-geordneten Slot-Listen der Galerie.
   * Jede Liste ist unabhängig 0-basiert; Index 0 = Variantenbild (bleibt).
   * @param {Element} gallery
   * @returns {{items: Element[], kind: string}[]}
   */
  #collectLists(gallery) {
    const lists = [];
    const push = (nodeList, kind) => {
      const items = Array.from(nodeList);
      if (items.length) lists.push({ items, kind });
    };

    // Slideshow (Karussell + Mobil-Ansicht des Grids): genau eine Slide-Reihe.
    const slides = gallery.querySelectorAll('slideshow-slide');
    if (slides.length) lists.push({ items: Array.from(slides), kind: 'media' });

    // Grid-Ansicht (Desktop).
    gallery.querySelectorAll('.media-gallery__grid').forEach((ul) => {
      push(ul.querySelectorAll(':scope > li'), 'media');
    });

    // Zoom-Dialog Hauptbilder.
    gallery.querySelectorAll('.dialog-zoomed-gallery').forEach((ul) => {
      push(ul.querySelectorAll(':scope > li'), 'media');
    });

    // Slideshow-Thumbnails (kann Desktop + Mobil = zwei Listen sein).
    gallery.querySelectorAll('.slideshow-controls__thumbnails').forEach((c) => {
      push(c.querySelectorAll(':scope > .slideshow-controls__thumbnail'), 'thumb');
    });

    // Zoom-Dialog Thumbnails.
    gallery.querySelectorAll('.dialog-thumbnails-list').forEach((c) => {
      push(c.querySelectorAll(':scope > .dialog-thumbnails-list__thumbnail'), 'thumb');
    });

    return lists;
  }

  /**
   * Positionen, die kein Bild sind - Video, externes Video, 3D-Modell.
   * Ermittelt an der Hauptmedienliste, weil nur dort das Medium selbst steht;
   * die Thumbnails tragen bloss ein <img> plus Play-Symbol.
   *
   * Bewusst nur ueber Medientyp und echte Medien-Elemente, nie ueber
   * Klassennamen: ein Bild mit unserem Video-Overlay traegt ebenfalls ein
   * Element mit "play" im Namen und ist trotzdem ein Bild-Slot.
   *
   * @param {Element} gallery
   * @returns {Set<number>}
   */
  #nonImagePositions(gallery) {
    const skip = new Set();
    const haupt =
      gallery.querySelectorAll('slideshow-slide').length
        ? gallery.querySelectorAll('slideshow-slide')
        : (gallery.querySelector('.media-gallery__grid')
            ? gallery.querySelector('.media-gallery__grid').querySelectorAll(':scope > li')
            : []);
    Array.from(haupt).forEach((el, i) => {
      const typ = (el.getAttribute('data-media-type') || '').toLowerCase();
      if (typ && typ !== 'image') {
        skip.add(i);
        return;
      }
      if (el.querySelector('video, iframe, model-viewer, deferred-media, product-model')) {
        skip.add(i);
      }
    });
    return skip;
  }

  /**
   * @param {Element[]} items
   * @param {string} kind
   */
  #applyList(items, kind, skip) {
    const data = this.data;
    // Position 0 ist das Variantenbild und bleibt. Ab 1 wird der Reihe nach
    // getauscht - Video-, Modell- und externe Slots werden dabei uebersprungen,
    // nicht ueberschrieben. Sonst laege ein stilles Bild unter dem Play-Button
    // des Videos, und das Video waere aus der Galerie verschwunden.
    let n = 0;
    for (let i = 1; i < items.length && n < data.length; i++) {
      if (skip && skip.has(i)) continue;
      const entry = data[n];
      const item = items[i];
      n++;
      if (!entry || !item) continue;

      if (kind === 'thumb') {
        this.#swapImg(item.querySelector('img'), entry, true);
      } else {
        const img = item.querySelector('.product-media__image') || item.querySelector('img');
        this.#swapImg(img, entry, false);
        this.#setRatio(item, entry);
      }
    }
  }

  /**
   * @param {Element|null} img
   * @param {object} entry
   * @param {boolean} isThumb
   */
  #swapImg(img, entry, isThumb) {
    if (!img || !entry) return;

    // Video-Einträge: best-effort. Nur wenn eine Bildquelle (Poster) vorhanden
    // ist, wird das <img> getauscht — sonst Slot unangetastet lassen.
    if (entry.type === 'video' && !entry.src) return;
    if (!entry.src && !entry.srcset) return;

    if (entry.srcset) img.setAttribute('srcset', entry.srcset);
    if (entry.src) img.setAttribute('src', entry.src);
    if (!isThumb && entry.full) img.setAttribute('data-max-resolution', entry.full);
    if (typeof entry.alt === 'string' && entry.alt !== '') img.setAttribute('alt', entry.alt);
  }

  /**
   * Aktualisiert das Seitenverhältnis, damit ein abweichend proportioniertes
   * EN-Bild nicht verzerrt/beschnitten wird. Defensiv.
   * @param {Element} item
   * @param {object} entry
   */
  #setRatio(item, entry) {
    const ratio = Number(entry.ratio);
    if (!ratio || !isFinite(ratio)) return;
    try {
      const pm = item.matches?.('.product-media') ? item : item.querySelector('.product-media');
      pm?.style.setProperty('--ratio', String(ratio));
      const cont =
        item.closest?.('.product-media-container') ||
        (item.classList?.contains('product-media-container') ? item : null);
      cont?.style.setProperty('--media-preview-ratio', String(ratio));
    } catch (e) {
      /* noop */
    }
  }

  /** @returns {boolean} true wenn eine (noch unmarkierte) Galerie bespielt wurde. */
  #apply() {
    const gallery = this.#findGallery();
    if (!gallery) return false;
    if (gallery.dataset.glrVmApplied === this._stamp) return false; // idempotent pro Instanz+Variante
    if (!this.data || !this.data.length) {
      gallery.dataset.glrVmApplied = this._stamp;
      return false;
    }

    // Einmal bestimmen, welche Positionen keine Bilder sind. Alle Listen der
    // Galerie (Slides, Grid, Zoom, Thumbnails) fuehren dieselbe Medienreihe in
    // derselben Reihenfolge, deshalb gilt der Befund fuer alle.
    const skip = this.#nonImagePositions(gallery);
    for (const list of this.#collectLists(gallery)) {
      this.#applyList(list.items, list.kind, skip);
    }
    gallery.dataset.glrVmApplied = this._stamp;
    return true;
  }

  /** rAF-entprellter Re-Apply; liest die Variante neu ein. */
  #scheduleApply() {
    if (this._raf) return;
    this._raf = requestAnimationFrame(() => {
      this._raf = 0;
      const vid = this.#currentVariantId();
      this.data = this.#listFor(vid);
      this._stamp = 'v' + (vid || '0');
      this.#apply();
    });
  }

  /** MutationObserver: faengt den replaceWith der Galerie bei Variantenwechsel. */
  #observe() {
    const scope = this.section instanceof Element ? this.section : document.body;
    if (typeof MutationObserver === 'undefined') return;
    this.observer = new MutationObserver(() => this.#scheduleApply());
    this.observer.observe(scope, { childList: true, subtree: true });
  }

  /** Zusaetzlicher Trigger ueber das native ProductSelect-Event. */
  async #listenVariant() {
    try {
      const { StandardEvents } = await import('@shopify/events');
      if (this.section && StandardEvents?.productSelect) {
        this._pselName = StandardEvents.productSelect;
        this._onProductSelect = () => this.#scheduleApply();
        this.section.addEventListener(this._pselName, this._onProductSelect);
      }
    } catch (e) {
      /* Events nicht verfuegbar — MutationObserver genuegt. */
    }
  }

  /**
   * Waermt die Bilder der uebrigen Eintraege im Leerlauf in den Browser-Cache.
   * Niedrige Prioritaet, damit sie mit nichts auf dem kritischen Pfad
   * konkurrieren. Nur Bilder, keine Videos.
   */
  #preloadRest() {
    if (!this.wantPreload) return;
    const shown = new Set(this.data.map((e) => e.src));
    const rest = [];
    for (const e of this.entries) {
      for (const img of e.img || []) {
        if (img && img.src && !shown.has(img.src)) rest.push(img);
      }
    }
    for (const img of this.shared) {
      if (img && img.src && !shown.has(img.src)) rest.push(img);
    }
    if (!rest.length) return;

    const run = () => {
      for (const entry of rest) {
        const im = new Image();
        im.decoding = 'async';
        if ('fetchPriority' in im) im.fetchPriority = 'low';
        if (entry.srcset) im.srcset = entry.srcset;
        im.src = entry.src;
      }
    };
    const idle = () => {
      if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 4000 });
      else setTimeout(run, 1200);
    };
    if (document.readyState === 'complete') idle();
    else window.addEventListener('load', idle, { once: true });
  }
}

if (!customElements.get('glr-variant-media')) {
  customElements.define('glr-variant-media', GlrVariantMedia);
}
